import { getAllSceneNodes, type CurvaDocument } from "@/document/model/document";
import type { ComponentVariantNode, SceneNode, TextNode } from "@/document/model/nodes";
import { textWithCharacters, type RichTextModel, type TypeStyle } from "@/document/model/text";
import { finalizeDocumentGeometry } from "@/commands/nodeCommands";
import { BASIC_GOOGLE_FONTS, googleFontsStylesheetLoaded, loadGoogleFonts, warmGoogleFontDefinitions, type GoogleFontDefinition } from "@/lib/GoogleFontLoader";
import type { Rect } from "@/lib/math/rect";
import { getCachedTextLayout, invalidateTextLayoutCache } from "@/renderer/text/canvasKitText";
import { textEqual } from "./shellSelection";

export function documentFontDefinitions(document: CurvaDocument): GoogleFontDefinition[] {
  const textNodes = getAllSceneNodes(document).filter((node): node is TextNode => node.type === "text");
  return fontDefinitionsForTexts(textNodes.map(node => node.text));
}

export function mergeFontDefinitions(...lists: GoogleFontDefinition[][]): GoogleFontDefinition[] {
  const weightsByFamily = new Map<string, Set<number>>();
  for (const list of lists) {
    for (const { family, weights } of list) {
      const familyWeights = weightsByFamily.get(family) ?? new Set<number>();
      for (const weight of weights) {
        familyWeights.add(weight);
      }
      weightsByFamily.set(family, familyWeights);
    }
  }

  return [...weightsByFamily.entries()].map(([family, weights]) => ({
    family,
    weights: [...weights].sort((left, right) => left - right),
  }));
}

export async function resolveDocumentFonts(document: CurvaDocument): Promise<boolean> {
  const definitions = mergeFontDefinitions(BASIC_GOOGLE_FONTS, documentFontDefinitions(document));
  const alreadyLoaded = googleFontsStylesheetLoaded(definitions);
  await loadGoogleFonts(definitions);
  const documentDefinitions = documentFontDefinitions(document);
  if (documentDefinitions.length > 0) {
    await warmGoogleFontDefinitions(documentDefinitions);
  }
  if (!alreadyLoaded) {
    invalidateTextLayoutCache();
    return true;
  }
  return false;
}

export function warmDocumentFontBytes(document: CurvaDocument): Promise<unknown> {
  const definitions = documentFontDefinitions(document);
  if (definitions.length === 0) {
    return Promise.resolve();
  }
  return warmGoogleFontDefinitions(definitions);
}

export function warmTextFontBytes(text: RichTextModel): Promise<unknown> {
  return warmGoogleFontDefinitions(fontDefinitionsForTexts([text]));
}

export function updateTextCharactersInDocument(document: CurvaDocument, nodeId: string, characters: string, zoomBucket: number): CurvaDocument {
  let changed = false;
  const pages = document.pages.map(page => {
    const children = updateTextCharactersInNodes(page.children, nodeId, characters, zoomBucket, () => {
      changed = true;
    });
    return children === page.children ? page : { ...page, children };
  });

  if (!changed) {
    return document;
  }

  const nextDocument = {
    ...document,
    pages,
    revision: document.revision + 1,
    metadata: {
      ...document.metadata,
      updatedAt: new Date().toISOString(),
    },
  };
  // Route through the canonical geometry pass so editing text inside a
  // component instance records the change as a per-instance override (via
  // refreshComponentInstances) instead of a bare node mutation that the next
  // instance refresh would silently revert to the variant default.
  finalizeDocumentGeometry(nextDocument, [nodeId]);
  return nextDocument;
}

/**
 * Zoom bucket used when measuring text for logical (document-space) bounds.
 * The logical intrinsic size is zoom-independent, so a fixed bucket keeps the
 * layout cache warm and results deterministic regardless of camera zoom.
 */
export const TEXT_LAYOUT_BUCKET = 10;

/**
 * Intrinsic document-space bounds for an auto-sized text node, suitable for
 * registering as the document layer's text measurer.
 */
export function textIntrinsicBounds(node: Extract<SceneNode, { type: "text" }>): Rect {
  return textBoundsForLayout(node, node.text, TEXT_LAYOUT_BUCKET);
}

export function textBoundsForLayout(node: Extract<SceneNode, { type: "text" }>, text: RichTextModel, zoomBucket: number): Rect {
  if (text.textLayout === "FIXED_SIZE") {
    return node.bounds;
  }

  const layout = getCachedTextLayout({ ...node, text }, zoomBucket);
  if (text.textLayout === "AUTO_WIDTH") {
    return {
      ...node.bounds,
      width: Math.max(1, layout.width),
      height: Math.max(1, layout.height),
    };
  }

  return {
    ...node.bounds,
    height: Math.max(1, layout.height),
  };
}

function updateTextCharactersInNodes(
  nodes: SceneNode[],
  nodeId: string,
  characters: string,
  zoomBucket: number,
  markChanged: () => void,
): SceneNode[] {
  let changed = false;
  const nextNodes = nodes.map(node => {
    if (node.type === "text" && node.id === nodeId) {
      const nextText = textWithCharacters(node.text, characters);
      if (textEqual(node.text, nextText)) {
        return node;
      }

      changed = true;
      markChanged();
      const nextNode = { ...node, text: nextText };
      return { ...nextNode, bounds: textBoundsForLayout(nextNode, nextText, zoomBucket) };
    }

    if (node.type === "component") {
      const children = updateTextCharactersInNodes(node.children, nodeId, characters, zoomBucket, markChanged) as ComponentVariantNode[];
      if (children === node.children) {
        return node;
      }

      changed = true;
      return { ...node, children };
    }

    if (node.type === "frame" || node.type === "group" || node.type === "componentVariant" || node.type === "instance" || node.type === "boolean") {
      const currentChildren = node.type === "boolean" ? node.sourceNodes : node.children;
      const children = updateTextCharactersInNodes(currentChildren, nodeId, characters, zoomBucket, markChanged);
      if (children === currentChildren) {
        return node;
      }

      changed = true;
      return node.type === "boolean" ? { ...node, sourceNodes: children } : { ...node, children };
    }

    return node;
  });

  return changed ? nextNodes : nodes;
}

function fontDefinitionsForTexts(texts: RichTextModel[]): GoogleFontDefinition[] {
  const weightsByFamily = new Map<string, Set<number>>();
  for (const text of texts) {
    for (const style of textFontStyles(text)) {
      const weights = weightsByFamily.get(style.fontFamily) ?? new Set<number>();
      weights.add(style.fontWeight);
      weightsByFamily.set(style.fontFamily, weights);
    }
  }

  return [...weightsByFamily.entries()].map(([family, weights]) => ({
    family,
    weights: [...weights].sort((left, right) => left - right),
  }));
}

function textFontStyles(text: RichTextModel): TypeStyle[] {
  return [
    text.style,
    ...Object.values(text.styleOverrideTable).map(override => ({ ...text.style, ...override })),
  ];
}
